<template>
  <div class="news-edit">
    <div class="header">
      <h2>{{ isEdit ? '编辑工作动态' : '新增工作动态' }}</h2>
      <a-button type="primary" @click="handleBack">
        <i class="iconfont icon-fanhuiicon mr5"></i> 返回
      </a-button>
    </div>

    <div class="form-container">
      <a-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          layout="vertical"
          class="edit-form"
      >
        <a-form-item label="动态标题" name="title">
          <a-input
              v-model:value="formData.title"
              placeholder="请输入动态标题"
              :maxlength="100"
              show-count
              class="large-input"
          />
        </a-form-item>

        <a-form-item label="动态内容" name="content">
          <a-textarea
              v-model:value="formData.content"
              placeholder="请输入动态内容"
              :auto-size="{ minRows: 7, maxRows: 25 }"
              class="large-textarea"
          />
        </a-form-item>

        <a-form-item label="发布状态" name="status">
          <a-radio-group v-model:value="formData.status">
            <a-radio value="0">保存为草稿</a-radio>
            <a-radio value="1">立即发布</a-radio>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="附件上传" name="fileList">
          <fileUpload
              class="pl64"
              :updateTitle="'点击上传'"
              ref="uploadRef"
              accept=".doc,.docx,.pdf,.jpg,.png,.xls,.xlsx"
              :maxCount="5"
              pathName="news"
              @fileListChange="onFileListChange">
          </fileUpload>
          <p class="upload-tip">支持PDF、Word、Excel格式文件，单个文件大小不超过10MB</p>
        </a-form-item>

        <a-form-item class="form-actions">
          <a-button @click="handleBack">取消</a-button>
          <a-button class="ml10" type="primary" @click="handleSubmit">
            {{ isEdit ? '保存修改' : '确认新增' }}
          </a-button>
        </a-form-item>
      </a-form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message, Modal } from 'ant-design-vue';
import useUserStore from '@/store/modules/user';
import { add, getById, update } from "@/api/news";
import fileUpload from '@/components/fileUpload/index.vue';

const router = useRouter();
const route = useRoute();
const store = useUserStore();
const formRef = ref();
const fileList = ref([])
const uploadRef = ref();

// 判断是否为编辑模式
const isEdit = computed(() => !!route.query.id);

// 表单数据
const formData = ref({
  id: null,
  title: '',
  content: '',
  status: '0', // 默认为草稿
  attachments: []
});

// 表单验证规则
const formRules = {
  title: [
    { required: true, message: '请输入动态标题', trigger: 'blur' },
    { min: 1, max: 100, message: '标题长度不能超过100个字符', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入动态内容', trigger: 'blur' }
  ]
};

// 文件上传列表变化处理
const onFileListChange = (fileList) => {
  formData.value.attachments = fileList.map(file => file.url);
};

// 返回上一页
const handleBack = () => {
  router.push('/news/news');
};

// 表单提交
const handleSubmit = () => {
  formRef.value.validate().then(() => {
    // 构造提交数据
    const submitData = {
      ...formData.value,
      status: parseInt(formData.value.status),
      // 创建人信息等
      createUser: store.userInfo.id,
      createUserName: store.userInfo.realName,
      createDept: store.deptId
    };

    // 调用对应的API
    const apiCall = isEdit.value ? update(submitData) : add(submitData);

    apiCall
      .then(res => {
        if (res.code === 200) {
          message.success(isEdit.value ? '工作动态更新成功' : '工作动态新增成功');
          // 返回列表页
          router.push('/news/news');
        } else {
          message.error(res.message || '操作失败');
        }
      })
      .catch(error => {
        message.error('操作失败: ' + error.message);
        console.error('操作失败:', error);
      });
  }).catch(errorInfo => {
    console.log('表单验证失败:', errorInfo);
  });
};

// 加载编辑数据
const loadEditData = () => {
  if (!isEdit.value) return;

  const newsId = route.query.id;
  getById(newsId)
    .then(res => {
      if (res.code === 200) {
        const data = res.data;
        // 设置表单数据
        formData.value = {
          id: data.id,
          title: data.title,
          content: data.content,
          status: data.status.toString(),
          attachments: data.attachments || []
        };
        // 初始化文件上传组件
        if (uploadRef.value && data.attachments && data.attachments.length > 0) {
          const initialFileList = data.attachments.map(url => ({
            uid: Math.random().toString(36).substring(2, 15),
            name: url.split('/').pop(),
            status: 'done',
            url: url
          }));
          uploadRef.value.setFileList(initialFileList);
        }
      } else {
        message.error(res.message || '获取工作动态详情失败');
      }
    })
    .catch(error => {
      message.error('获取工作动态详情失败: ' + error.message);
    });
};

onMounted(() => {
  loadEditData();
});
</script>

<style lang="less" scoped>
.news-edit {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header h2 {
  font-size: 18px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin: 0;
}

.form-container {
  background-color: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
}

.edit-form {
  max-width: 800px;
  margin: 0 auto;
}

.large-input {
  width: 100%;
}

.large-textarea {
  width: 100%;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.form-actions {
  margin-top: 32px;
  text-align: center;
}
</style>